import React, { memo } from 'react';
import './DishCard.scss';
import SvgIcon from '../SvgIcon/SvgIcon';
import PropTypes from 'prop-types';
import DishQuantity from '../DishQuantity/DishQuantity';
import { connect } from 'react-redux';
import { DISH_DETAILS_MODAL } from '@store/constants/modal';
import { openModal } from '@store/actions/modal';
import { addDish, removeDish, removeDishWithOptions, toggleLikeDish } from '@store/actions/menu';
import Colors from '@data/enums/Color.enum';
import currency from '@filters/currency';
import Button from 'view/components/inputs/Button/Button';
import { calculateDishPrice, getPlaceOrders, getSelectedOptionsNames } from '@utils/menu/menu'
import { getUserLang } from '@utils/url/urlUtils';

const DishCard = ({
                    dish,
                    short = false,
                    className = '',
                    showDishDetails,
                    toggleLikeDish,
                    order,
                    addDish,
                    removeDish,
                    removeDishWithOptions,
                  }) => {
  const selectedOptions = short && order ? getSelectedOptionsNames(order, dish) : [];

  // Check if dish has options
  const hasOptions = dish.dishOptionCategories && dish.dishOptionCategories.length > 0;

  // Get current order for this dish (if not provided as prop)
  let currentOrder, currentCount;

  if (order) {
    // If order is provided as prop (e.g., in cart modal), use it directly
    currentOrder = order;
    currentCount = currentOrder.count;
  } else {
    // For menu view, calculate total count for this dish
    const placeOrders = getPlaceOrders();
    if (hasOptions) {
      // For dishes with options, sum all orders for this dish ID
      currentCount = placeOrders
        .filter(o => o.id === dish.id)
        .reduce((total, o) => total + o.count, 0);
      // Use the first order as reference (for consistency)
      currentOrder = placeOrders.find(o => o.id === dish.id);
    } else {
      // For simple dishes, find the exact order
      currentOrder = placeOrders.find(o => o.id === dish.id);
      currentCount = currentOrder ? currentOrder.count : 0;
    }
  }

  const handleAddDish = (e) => {
    e.stopPropagation();
    addDish(dish);
  };

  const handleRemoveDish = (e) => {
    e.stopPropagation();
    if (hasOptions) {
      // For dishes with options, find the first order and remove it
      const placeOrders = getPlaceOrders();
      const firstOrder = placeOrders.find(o => o.id === dish.id);
      if (firstOrder) {
        removeDishWithOptions(firstOrder);
      }
    } else {
      removeDish(dish);
    }
  };

  const handleShowDishDetails = (e) => {
    e.stopPropagation();
    showDishDetails(dish);
  };

  // Render functions for different counter states
  const renderSimpleCounter = () => {
    if (currentCount === 0) {
      return (
        <div className='dish-card__counter-add' onClick={handleAddDish}>
          +
        </div>
      );
    }

    return (
      <div className='dish-card__counter-controls'>
        <div className='dish-card__counter-minus' onClick={handleRemoveDish}>
          -
        </div>
        <div className='dish-card__counter-value'>{currentCount}</div>
        <div className='dish-card__counter-plus' onClick={handleAddDish}>
          +
        </div>
      </div>
    );
  };

  const renderOptionsCounter = () => {
    if (currentCount === 0) {
      return (
        <Button
          inputClass='dish-card__button-submit'
          onClick={handleShowDishDetails}
        >
          +
        </Button>
      );
    }

    return (
      <div className='dish-card__count-badge'>
        <div className='dish-card__count-badge-value'>{currentCount}</div>
        <Button
          inputClass='dish-card__button-submit'
          onClick={handleShowDishDetails}
        >
          +
        </Button>
      </div>
    );
  };

  const renderContent = () => {
    if (short && selectedOptions.length > 0) {
      return (
        <div className='dish-card__selected-options'>
          {selectedOptions.map((option, index) => (
            <div key={index} className='dish-card__selected-option'>
              {option}
            </div>
          ))}
        </div>
      );
    }

    return <div className='dish-card__description'>{dish.description}</div>;
  };

  const renderAllergens = () => {
    if (!dish.allergens?.length) return null;

    const currentLang = getUserLang();

    return dish.allergens.map((a) => {
      const allergenName = a.trs?.[currentLang]?.name || a.name;
      const allergenDescription = a.trs?.[currentLang]?.description || a.description;

      return (
        <div className='dish-card__allergens' key={a.id}>
          <SvgIcon type={a.icon.replace('.svg', '')} className='dish-card__allergens-icon' />
          <div className='dish-card__allergens-tooltip'>
            <div className='dish-card__allergens-name'>{allergenName}</div>
            {allergenDescription && (
              <div className='dish-card__allergens-description'>{allergenDescription}</div>
            )}
          </div>
        </div>
      );
    });
  };

  const renderTags = () => {
    if (short) return null;

    return (
      <div className='dish-card__tags'>
        {dish.weight && <div className='dish-card__weight'>{dish.weight}</div>}
        {dish.variables?.hot && (
          <div className='dish-card__tag'>
            <SvgIcon type='hot' />
          </div>
        )}
        {dish.variables?.vegetarian && (
          <div className='dish-card__tag'>
            <SvgIcon type='vegetarian' />
          </div>
        )}
        {renderAllergens()}
      </div>
    );
  };

  const renderPhoto = () => {
    if (!dish.photo || short) return null;

    return (
      <div className='dish-card__photo-wrapper'>
        <img className='dish-card__photo' src={dish.photoUrl} alt={dish.name} />
      </div>
    );
  };

  const renderLikeButton = () => {
    if (short) return null;

    return (
      <div
        className={`dish-card__like-wrapper ${dish.isLiked ? 'dish-card__like-wrapper--liked' : ''}`}
        onClick={(e) => toggleLikeDish(e, dish)}>
        <SvgIcon
          type='heart'
          filled={dish.isLiked}
          fill={dish.isLiked ? 'white' : Colors.COLOR_PRIMARY}
          iconProps={{ size: 16 }}
        />
      </div>
    );
  };

  const renderShortPrice = () => {
    if (!short) return null;

    return (
      <div className='dish-card__short-price'>
        <div className='dish-card__price dish-card__price--small'>{currency(calculateDishPrice(dish, order))}</div>
        <DishQuantity order={order} className={short ? 'dish-card__dish-quantity-shotr' : ''} />
      </div>
    );
  };

  const renderPrice = () => {
    if (short) return null;

    return <div className='dish-card__price'>{currency(dish.price)}</div>;
  };

  return (
    <div className={`dish-card ${short ? 'dish-card--short' : ''} ${className}`}
      onClick={() => {
        if (!short) showDishDetails(dish);
      }}>
      <div className='dish-card__content-wrapper'>
        {renderPhoto()}
        <div className='dish-card__content'>
          <div className='dish-card__name-row'>
            {dish.variables?.top && <SvgIcon type='top' />}
            <div className='dish-card__name'>{dish.name}</div>
          </div>
          {renderTags()}
          {renderContent()}
        </div>
      </div>
      <div className={`dish-card__actions-wrapper ${short ? 'dish-card__actions-wrapper--short' : ''}`}>
        {renderPrice()}
        <div className={`dish-card__actions ${short ? 'dish-card__actions--short' : ''}`}>
          {renderLikeButton()}
          {!short && hasOptions && (
            <div className='dish-card__options-counter'>
              {renderOptionsCounter()}
            </div>
          )}
          {!short && !hasOptions && (
            <div className='dish-card__simple-counter'>
              {renderSimpleCounter()}
            </div>
          )}
        </div>
        {renderShortPrice()}
      </div>
    </div>
  );
};

DishCard.propTypes = {
  dish: PropTypes.object.isRequired,
  className: PropTypes.string,
  showDishDetails: PropTypes.func.isRequired,
  toggleLikeDish: PropTypes.func.isRequired,
  short: PropTypes.bool,
  order: PropTypes.shape({
    id: PropTypes.number,
    count: PropTypes.number,
    options: PropTypes.arrayOf(PropTypes.shape({
      count: PropTypes.number,
      option: PropTypes.shape({
        id: PropTypes.number
      })
    }))
  }),
  addDish: PropTypes.func.isRequired,
  removeDish: PropTypes.func.isRequired,
  removeDishWithOptions: PropTypes.func.isRequired,
  orders: PropTypes.object,
};

const mapStateToProps = ({ place }) => ({
  place: place.details.data,
  orders: place.menu, // needed to re-render component in case of order updates
});

const mapDispatchToProps = {
  showDishDetails: (meta) => openModal(DISH_DETAILS_MODAL, meta),
  addDish,
  removeDish,
  removeDishWithOptions,
  toggleLikeDish,
};

export default memo(connect(mapStateToProps, mapDispatchToProps)(DishCard));
